import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import {
  LISTING_TYPES,
  PRICE_VERIFICATION_STATUSES,
  isTicketRequest,
  getTicketRequestDetails,
  getPriceRange,
} from '../../util/listingTypes';
import { formatLocationShort } from '../../util/stateNames';
import verifiedPriceIcon from '../../assets/Verified_Price_icon.svg';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listing } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);

  // Check if this is a Reverse listing (ticket request)
  const isReverseListing = listing && isTicketRequest(listing);

  if (isReverseListing) {
    // For Reverse listings, show price range from custom fields
    const priceRange = getPriceRange(listing);
    if (priceRange) {
      return (
        <div className={css.price}>
          <div className={css.priceValue} title={priceRange}>
            {priceRange}
          </div>
          <div className={css.requestBadge}>
            <FormattedMessage
              id="ListingCard.lookingForTickets"
              defaultMessage="Looking for tickets"
            />
          </div>
        </div>
      );
    }
    return (
      <div className={css.price}>
        <div className={css.requestBadge}>
          <FormattedMessage
            id="ListingCard.lookingForTickets"
            defaultMessage="Looking for tickets"
          />
        </div>
      </div>
    );
  }

  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Check if this is a Reverse listing (ticket request)
  const isReverseListing = isTicketRequest(currentListing);
  const isTicketSaleListing = publicData?.listingType === LISTING_TYPES.TICKET_SALE;
  const priceVerificationStatus =
    publicData?.price_verification_status || PRICE_VERIFICATION_STATUSES.UNVERIFIED;
  const isPriceVerified = priceVerificationStatus === PRICE_VERIFICATION_STATUSES.VERIFIED;
  const requestDetails = isReverseListing ? getTicketRequestDetails(currentListing) : null;

  // Format date - prefer event_date from custom fields for Reverse listings
  const eventDate =
    isReverseListing && requestDetails?.eventDate
      ? requestDetails.eventDate
      : publicData?.eventDateTime;

  const containsTime = eventDate?.includes('T');
  const formattedDate = eventDate
    ? containsTime
      ? new Date(eventDate).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        })
      : new Date(eventDate).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
    : null;

  // For Reverse listings, use venue and event_location from custom fields
  const venueName =
    isReverseListing && requestDetails?.venue ? requestDetails.venue : publicData?.venueName;
  const location =
    isReverseListing && requestDetails?.eventLocation
      ? formatLocationShort(requestDetails.eventLocation)
      : (() => {
          const venueLocation = publicData?.venueLocation?.trim() || '';
          const city = publicData?.city?.trim() || '';
          const state = publicData?.state?.trim() || '';
          if (venueLocation) {
            return formatLocationShort(venueLocation);
          }
          if (city && state) {
            return formatLocationShort(`${city}, ${state}`);
          }
          return city || (state ? formatLocationShort(state) : '');
        })();

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        {isTicketSaleListing && isPriceVerified ? (
          <span className={css.priceVerificationBadge} aria-label="Verified price">
            <img
              className={css.priceVerificationBadgeImage}
              src={verifiedPriceIcon}
              alt=""
              aria-hidden="true"
              draggable="false"
            />
          </span>
        ) : null}
      </AspectRatioWrapper>
      <div className={css.info}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listing={currentListing}
        />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo && (venueName || location || formattedDate) ? (
            <div className={css.authorInfo}>
              {venueName && <span>{venueName}</span>}
              {venueName && location && <span>, </span>}
              {location && <span>{location}</span>}
              {formattedDate && (venueName || location) && <span> | </span>}
              {formattedDate && <span>{formattedDate}</span>}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
